import { MedosClient } from "../client/MedosClient";
import { ENDPOINTS } from "../constants/endpoints";
export const PROOF_MAX_BYTES = 5 * 1024 * 1024;
export const PROOF_ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp"];
export function isPayableMethod(m) {
    return Boolean(m?.qrImageUrl || m?.paymentLink || m?.kind);
}
export function normalizeManualPaymentMethod(m) {
    const qrImageUrl = m?.qrImageUrl ?? null;
    return {
        id: Number(m?.id),
        name: m?.name ?? "",
        kind: m?.kind ?? (qrImageUrl ? "QR" : "UPI"),
        provider: m?.provider ?? null,
        qrImageUrl,
        paymentLink: m?.paymentLink ?? null,
        currency: m?.currency ?? null,
        instructions: m?.instructions ?? null,
        displayOrder: m?.displayOrder ?? null,
    };
}
export function parseManualPaymentMethods(raw) {
    const payload = raw?.data ?? raw;
    const methods = Array.isArray(payload?.qrPaymentMethods)
        ? payload.qrPaymentMethods
        :
            (Array.isArray(payload) ? payload : (payload?.methods ?? [])).filter(isPayableMethod);
    return methods.map(normalizeManualPaymentMethod);
}
export class PaymentService {
    static async loadRazorpayScript() {
        return new Promise((resolve) => {
            if (window.Razorpay) {
                resolve(true);
                return;
            }
            const script = document.createElement("script");
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.async = true;
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    }
    static async openPaymentModal(orderId, amount, customerEmail, customerPhone, customerName, onSuccess, onError, keyId, currency = "INR") {
        try {
            const isLoaded = await this.loadRazorpayScript();
            if (!isLoaded) {
                throw new Error("Failed to load Razorpay script");
            }
            const Razorpay = window.Razorpay;
            const options = {
                key: keyId,
                order_id: orderId,
                amount: amount,
                currency: currency,
                name: "Medos Healthcare",
                description: "Appointment Booking",
                customer_details: {
                    name: customerName,
                    email: customerEmail,
                    contact: customerPhone,
                },
                handler: (response) => {
                    onSuccess(response);
                },
                modal: {
                    ondismiss: () => {
                        onError(new Error("Payment cancelled by user"));
                    },
                },
                prefill: {
                    name: customerName,
                    email: customerEmail,
                    contact: customerPhone,
                },
                theme: {
                    color: "#3b82f6",
                },
            };
            const razorpayInstance = new Razorpay(options);
            razorpayInstance.open();
        }
        catch (error) {
            onError(error instanceof Error
                ? error
                : new Error("Failed to open payment modal"));
        }
    }
    static verifyPaymentSignature(paymentId, orderId, signature) {
        console.warn("Payment signature verification should be done on the backend for security");
        return !!(paymentId && orderId && signature);
    }
    static async fetchPaymentMethods() {
        const client = await MedosClient.ensureInitialized();
        const res = await client.get(ENDPOINTS.payments.methods, {
            baseURL: MedosClient.getApiBaseUrlV2(),
        });
        return parseManualPaymentMethods(res.data);
    }
    static async submitPaymentClaim(appointmentId, payload, idempotencyKey) {
        const client = await MedosClient.ensureInitialized();
        const res = await client.post(ENDPOINTS.payments.claim(appointmentId), payload, {
            baseURL: MedosClient.getApiBaseUrlV2(),
            headers: {
                "Content-Type": "application/json",
                "Idempotency-Key": idempotencyKey,
            },
        });
        const raw = res.data;
        return (raw?.data ?? raw);
    }
    static async uploadPaymentProof(appointmentId, file) {
        const client = await MedosClient.ensureInitialized();
        const form = new FormData();
        form.append("file", file);
        const res = await client.post(ENDPOINTS.payments.claimProof(appointmentId), form, { baseURL: MedosClient.getApiBaseUrlV2() });
        const raw = res.data;
        return (raw?.data?.url ?? raw?.url ?? "");
    }
    static validateProofFile(file) {
        if (!PROOF_ALLOWED_TYPES.includes(file.type)) {
            return "Upload a PNG, JPEG or WebP image.";
        }
        if (file.size > PROOF_MAX_BYTES) {
            return "That image is over 5 MB. Try a smaller screenshot.";
        }
        return null;
    }
}
export default PaymentService;
